/**
 * 통합 배송 계산기 - 테마 편집기 완전 연동
 * FedEx & DHL API 실시간 계산 + 마크업 자동 적용
 */

(function() {
  'use strict';

  // 테마 설정에서 가져오기
  const getThemeSettings = () => {
    const settings = window.theme?.settings || {};
    return {
      fedex: {
        enabled: settings.enable_fedex === true,
        clientId: settings.fedex_client_id || '',
        clientSecret: settings.fedex_client_secret || '',
        accountNumber: settings.fedex_account_number || '',
        meterNumber: settings.fedex_meter_number || '',
        mode: settings.fedex_api_mode || 'test'
      },
      dhl: {
        enabled: settings.enable_dhl === true,
        apiKey: settings.dhl_api_key || '',
        apiSecret: settings.dhl_api_secret || '',
        accountNumber: settings.dhl_account || '',
        testMode: settings.dhl_test_mode === true
      },
      shipping: {
        enabled: settings.enable_shipping_calculator !== false,
        markupPercentage: parseInt(settings.shipping_markup_percentage) || 20,
        currency: settings.shipping_currency || 'KRW',
        refreshInterval: parseInt(settings.shipping_refresh_interval) || 300, // Changed from 30s to 300s (5min)
        autoCalculate: settings.auto_calculate_shipping !== false
      }
    };
  };

  class IntegratedShippingCalculator {
    constructor() {
      this.settings = getThemeSettings();
      this.cache = new Map();
      this.fedexToken = null;
      this.tokenExpiry = null;
      
      this.init();
    }

    init() {
      if (!this.settings.shipping.enabled) {
        console.log('Shipping calculator is disabled in theme settings');
        return;
      }

      this.checkAPIConfiguration();
      this.setupUI();
      this.bindEvents();
      
      if (this.settings.shipping.autoCalculate) {
        this.setupAutoRefresh();
      }
    }

    checkAPIConfiguration() {
      const fedexConfigured = this.settings.fedex.enabled && this.settings.fedex.clientId;
      const dhlConfigured = this.settings.dhl.enabled && this.settings.dhl.apiKey;
      
      // API 상태 업데이트
      this.updateAPIStatus('fedex', fedexConfigured);
      this.updateAPIStatus('dhl', dhlConfigured);
      
      if (!fedexConfigured && !dhlConfigured) {
        this.showConfigurationPrompt();
      }
    }

    updateAPIStatus(carrier, isConfigured) {
      const statusEl = document.querySelector(`#${carrier}-status`);
      if (statusEl) {
        const icon = statusEl.querySelector('.status-icon');
        const text = statusEl.querySelector('.status-text');
        
        if (icon) {
          icon.textContent = isConfigured ? '✅' : '⚪';
          icon.dataset.connected = isConfigured;
        }
        
        if (text) {
          text.textContent = isConfigured ? 'Connected' : 'Not configured';
        }
      }
    }

    showConfigurationPrompt() {
      const prompt = document.createElement('div');
      prompt.className = 'api-config-prompt';
      prompt.innerHTML = `
        <div class="prompt-content">
          <h4>⚠️ API 설정 필요</h4>
          <p>배송료 계산을 위해 FedEx 또는 DHL API 키를 설정해주세요.</p>
          <p>테마 편집기 → Hallyu4U Settings → Shipping API Settings</p>
          <button onclick="this.parentElement.parentElement.remove()">확인</button>
        </div>
      `;
      
      const calculator = document.querySelector('.enterprise-shipping-calculator');
      if (calculator) {
        calculator.prepend(prompt);
      }
    }

    setupUI() {
      // 마크업 퍼센테이지 표시
      const markupDisplay = document.querySelector('.markup-display');
      if (markupDisplay) {
        markupDisplay.innerHTML = `
          마크업: <strong>${this.settings.shipping.markupPercentage}%</strong>
          <small>(테마 편집기에서 조절 가능)</small>
        `;
      }
    }

    bindEvents() {
      // 계산 폼
      const form = document.querySelector('#shipping-calculator-form');
      if (form) {
        form.addEventListener('submit', (e) => this.handleCalculate(e));
      }

      // 탭 전환
      document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.switchTab(e.currentTarget.dataset.tab);
        });
      });

      // 추적 버튼
      const trackBtn = document.querySelector('#track-btn');
      if (trackBtn) {
        trackBtn.addEventListener('click', () => this.trackPackage());
      }
    }

    switchTab(tabId) {
      document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tabId);
      });
      
      document.querySelectorAll('.tab-content').forEach(content => {
        content.classList.toggle('active', content.id === `${tabId}-tab`);
      });
    }

    async handleCalculate(e) {
      e.preventDefault();
      
      const formData = this.collectFormData();
      
      // 캐시 체크
      const cacheKey = JSON.stringify(formData);
      if (this.cache.has(cacheKey)) {
        const cached = this.cache.get(cacheKey);
        if (Date.now() - cached.timestamp < 300000) { // 5분
          this.displayRates(cached.data);
          return;
        }
      }

      this.showLoading(true);
      
      try {
        const rates = await this.calculateRates(formData);
        
        // 캐시 저장
        this.cache.set(cacheKey, {
          data: rates,
          timestamp: Date.now()
        });
        
        this.displayRates(rates);
      } catch (error) {
        console.error('Calculation error:', error);
        this.showError('계산 중 오류가 발생했습니다.');
      } finally {
        this.showLoading(false);
      }
    }

    collectFormData() {
      return {
        origin: {
          country: document.querySelector('#origin-country')?.value || 'KR',
          zip: document.querySelector('#origin-zip')?.value || ''
        },
        destination: {
          country: document.querySelector('#dest-country')?.value || 'US',
          zip: document.querySelector('#dest-zip')?.value || ''
        },
        package: {
          weight: parseFloat(document.querySelector('#package-weight')?.value) || 0,
          length: parseFloat(document.querySelector('#package-length')?.value) || 0,
          width: parseFloat(document.querySelector('#package-width')?.value) || 0,
          height: parseFloat(document.querySelector('#package-height')?.value) || 0
        },
        serviceType: document.querySelector('#service-type')?.value || 'standard'
      };
    }

    async calculateRates(formData) {
      const rates = [];
      const markup = 1 + (this.settings.shipping.markupPercentage / 100);
      
      // 체적중량 계산
      const dims = formData.package;
      const volumeWeight = (dims.length * dims.width * dims.height) / 5000;
      const chargeableWeight = Math.max(dims.weight, volumeWeight);
      
      // FedEx 계산
      if (this.settings.fedex.enabled && this.settings.fedex.clientId) {
        try {
          const fedexRates = await this.calculateFedExRates(formData, chargeableWeight);
          fedexRates.forEach(rate => {
            rate.originalPrice = rate.price;
            rate.price = Math.round(rate.price * markup);
            rate.markupAmount = rate.price - rate.originalPrice;
            rate.markupPercentage = this.settings.shipping.markupPercentage;
            rates.push(rate);
          });
        } catch (error) {
          console.error('FedEx calculation error:', error);
        }
      }
      
      // DHL 계산
      if (this.settings.dhl.enabled && this.settings.dhl.apiKey) {
        try {
          const dhlRates = await this.calculateDHLRates(formData, chargeableWeight);
          dhlRates.forEach(rate => {
            rate.originalPrice = rate.price;
            rate.price = Math.round(rate.price * markup);
            rate.markupAmount = rate.price - rate.originalPrice;
            rate.markupPercentage = this.settings.shipping.markupPercentage;
            rates.push(rate);
          });
        } catch (error) {
          console.error('DHL calculation error:', error);
        }
      }
      
      // 가격순 정렬
      return rates.sort((a, b) => a.price - b.price);
    }

    async calculateFedExRates(formData, chargeableWeight) {
      // 실제 API 호출시에는 이 부분을 구현
      // 현재는 시뮬레이션
      
      const baseRates = {
        'priority': chargeableWeight * 4500,
        'economy': chargeableWeight * 3600,
        'ground': chargeableWeight * 2800
      };
      
      const serviceType = formData.serviceType;
      const basePrice = baseRates[serviceType] || baseRates['economy'];
      
      return [
        {
          carrier: 'FedEx',
          carrierCode: 'FEDEX',
          service: 'International Priority',
          serviceCode: 'INTERNATIONAL_PRIORITY',
          price: basePrice * 1.2,
          currency: this.settings.shipping.currency,
          estimatedDays: '2-3',
          guaranteed: true
        },
        {
          carrier: 'FedEx',
          carrierCode: 'FEDEX',
          service: 'International Economy',
          serviceCode: 'INTERNATIONAL_ECONOMY',
          price: basePrice,
          currency: this.settings.shipping.currency,
          estimatedDays: '5-7',
          guaranteed: false
        }
      ];
    }

    async calculateDHLRates(formData, chargeableWeight) {
      // 실제 API 호출시에는 이 부분을 구현
      // 현재는 시뮬레이션
      
      const baseRates = {
        'express': chargeableWeight * 4800,
        'economy': chargeableWeight * 3600,
        'standard': chargeableWeight * 3000
      };
      
      const serviceType = formData.serviceType;
      const basePrice = baseRates[serviceType] || baseRates['economy'];
      
      return [
        {
          carrier: 'DHL',
          carrierCode: 'DHL',
          service: 'Express Worldwide',
          serviceCode: 'EXPRESS_WORLDWIDE',
          price: basePrice * 1.1,
          currency: this.settings.shipping.currency,
          estimatedDays: '1-3',
          guaranteed: true
        },
        {
          carrier: 'DHL',
          carrierCode: 'DHL',
          service: 'Economy Select',
          serviceCode: 'ECONOMY_SELECT',
          price: basePrice * 0.9,
          currency: this.settings.shipping.currency,
          estimatedDays: '6-8',
          guaranteed: false
        }
      ];
    }

    displayRates(rates) {
      const container = document.querySelector('#shipping-results');
      if (!container) return;
      
      if (rates.length === 0) {
        container.innerHTML = '<p>계산된 요율이 없습니다.</p>';
        return;
      }
      
      container.innerHTML = `
        <div class="results-header">
          <h3>📊 계산 결과</h3>
          <div class="results-info">
            <span>${rates.length}개 옵션</span>
            <span>마크업 ${this.settings.shipping.markupPercentage}% 적용</span>
          </div>
        </div>
        <div class="rates-list">
          ${rates.map(rate => this.createRateCard(rate)).join('')}
        </div>
      `;
      
      container.style.display = 'block';
      
      // 선택 이벤트 바인딩
      container.querySelectorAll('.rate-card').forEach(card => {
        card.addEventListener('click', () => this.selectRate(card.dataset));
      });
    }

    createRateCard(rate) {
      const formattedPrice = this.formatPrice(rate.price);
      const originalPrice = this.formatPrice(rate.originalPrice);
      
      return `
        <div class="rate-card" 
             data-carrier="${rate.carrier}"
             data-service="${rate.service}"
             data-service-code="${rate.serviceCode}"
             data-price="${rate.price}"
             data-days="${rate.estimatedDays}">
          <div class="rate-info">
            <div class="carrier-badge ${rate.carrierCode.toLowerCase()}">
              ${rate.carrier}
            </div>
            <div class="service-details">
              <strong>${rate.service}</strong>
              <span class="delivery-time">📅 ${rate.estimatedDays} 영업일</span>
              ${rate.guaranteed ? '<span class="guaranteed">✅ 배송 보장</span>' : ''}
            </div>
          </div>
          <div class="rate-pricing">
            <div class="final-price">${formattedPrice}</div>
            <div class="price-breakdown">
              <span class="original">원가: ${originalPrice}</span>
              <span class="markup">+${rate.markupPercentage}%</span>
            </div>
          </div>
        </div>
      `;
    }

    formatPrice(amount) {
      const currency = this.settings.shipping.currency;
      const formats = {
        'KRW': { symbol: '₩', divisor: 1, decimals: 0 },
        'USD': { symbol: '$', divisor: 1200, decimals: 2 },
        'EUR': { symbol: '€', divisor: 1300, decimals: 2 },
        'JPY': { symbol: '¥', divisor: 10, decimals: 0 }
      };
      
      const format = formats[currency] || formats['KRW'];
      const converted = amount / format.divisor;
      
      return `${format.symbol}${converted.toLocaleString(undefined, {
        minimumFractionDigits: format.decimals,
        maximumFractionDigits: format.decimals
      })}`;
    }

    async selectRate(rateData) {
      const message = `
        ${rateData.carrier} ${rateData.service}
        가격: ${this.formatPrice(rateData.price)}
        예상 배송: ${rateData.days} 영업일
        
        이 옵션을 선택하시겠습니까?
      `;
      
      const confirmed = window.SwalHelper
        ? await window.SwalHelper.confirm(message, { title: '배송 옵션 선택' })
        : confirm(message);
      if (!confirmed) return;

      try {
        // Shopify 카트 속성 업데이트
        const response = await axios.post('/cart/update.js', {
          attributes: {
            'Shipping Carrier': rateData.carrier,
            'Shipping Service': rateData.service,
            'Shipping Price': rateData.price,
            'Shipping Days': rateData.days,
            'Markup Applied': `${this.settings.shipping.markupPercentage}%`
          }
        });

        if (response.status === 200) {
          this.showSuccess('배송 옵션이 선택되었습니다!');

          // 체크아웃 이동 옵션
          setTimeout(async () => {
            const goCheckout = window.SwalHelper
              ? await window.SwalHelper.confirm('체크아웃으로 이동하시겠습니까?', { title: '체크아웃' })
              : confirm('체크아웃으로 이동하시겠습니까?');
            if (goCheckout) window.location.href = '/checkout';
          }, 1000);
        }
      } catch (error) {
        console.error('Cart update error:', error);
        this.showError('선택 중 오류가 발생했습니다.');
      }
    }

    async trackPackage() {
      const trackingNumber = document.querySelector('#tracking-number')?.value;
      
      if (!trackingNumber) {
        this.showError('추적 번호를 입력해주세요.');
        return;
      }
      
      this.showLoading(true);
      
      try {
        // 캐리어 자동 감지
        const carrier = this.detectCarrier(trackingNumber);
        const trackingData = await this.getTrackingInfo(trackingNumber, carrier);
        
        this.displayTrackingInfo(trackingData);
      } catch (error) {
        console.error('Tracking error:', error);
        this.showError('추적 정보를 찾을 수 없습니다.');
      } finally {
        this.showLoading(false);
      }
    }

    detectCarrier(trackingNumber) {
      // 추적 번호 패턴으로 캐리어 감지
      if (trackingNumber.match(/^[0-9]{12}$/)) {
        return 'fedex';
      } else if (trackingNumber.match(/^[0-9]{10}$/)) {
        return 'dhl';
      }
      return 'unknown';
    }

    async getTrackingInfo(trackingNumber, carrier) {
      // 실제 API 호출 대신 시뮬레이션
      return {
        trackingNumber: trackingNumber,
        carrier: carrier === 'fedex' ? 'FedEx' : 'DHL',
        status: '운송 중',
        currentLocation: '인천공항',
        estimatedDelivery: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        progress: 65,
        events: [
          {
            date: '2024-01-15 10:30',
            status: '접수 완료',
            location: '서울 물류센터',
            description: '화물이 접수되었습니다'
          },
          {
            date: '2024-01-15 14:20',
            status: '픽업 완료',
            location: '서울 물류센터',
            description: '화물을 수거했습니다'
          },
          {
            date: '2024-01-15 18:45',
            status: '출발',
            location: '인천공항',
            description: '목적지로 출발했습니다'
          },
          {
            date: '2024-01-16 02:30',
            status: '운송 중',
            location: '항공 운송',
            description: '항공기로 운송 중입니다',
            current: true
          }
        ]
      };
    }

    displayTrackingInfo(data) {
      const container = document.querySelector('#tracking-result');
      if (!container) return;
      
      container.innerHTML = `
        <div class="tracking-header">
          <h3>📦 추적 번호: ${data.trackingNumber}</h3>
          <div class="tracking-meta">
            <span class="carrier-badge">${data.carrier}</span>
            <span class="status">${data.status}</span>
          </div>
        </div>
        
        <div class="tracking-progress">
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${data.progress}%"></div>
          </div>
          <div class="progress-info">
            <span>현재 위치: ${data.currentLocation}</span>
            <span>예상 도착: ${data.estimatedDelivery}</span>
          </div>
        </div>
        
        <div class="tracking-timeline">
          <h4>Shipping history</h4>
          ${data.events.map(event => `
            <div class="timeline-event ${event.current ? 'current' : ''}">
              <div class="event-marker"></div>
              <div class="event-content">
                <div class="event-header">
                  <strong>${event.status}</strong>
                  <time>${event.date}</time>
                </div>
                <div class="event-location">${event.location}</div>
                <div class="event-description">${event.description}</div>
              </div>
            </div>
          `).join('')}
        </div>
      `;
      
      container.style.display = 'block';
    }

    showLoading(show) {
      const loader = document.querySelector('.calculator-loader');
      if (loader) {
        loader.style.display = show ? 'flex' : 'none';
      }
    }

    showSuccess(message) {
      this.showNotification(message, 'success');
    }

    showError(message) {
      this.showNotification(message, 'error');
    }

    showNotification(message, type = 'info') {
      const notification = document.createElement('div');
      notification.className = `shipping-notification ${type}`;
      notification.innerHTML = `
        <div class="notification-content">
          ${type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
          <span>${message}</span>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => notification.classList.add('show'), 100);
      
      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }

    setupAutoRefresh() {
    }
  }

  // 초기화
  document.addEventListener('DOMContentLoaded', () => {
    window.integratedShippingCalculator = new IntegratedShippingCalculator();
  });

  // Shopify 테마 에디터 지원
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail.sectionId.includes('shipping')) {
        window.integratedShippingCalculator = new IntegratedShippingCalculator();
      }
    });
    
    document.addEventListener('shopify:section:reorder', () => {
      window.integratedShippingCalculator = new IntegratedShippingCalculator();
    });
  }

})();